/**
 * One of the store's product lists, as `GET /template-editor/collections`
 * lists it (`TemplateEditorCollectionDto`): the four defaults first, then the
 * merchant's own collections by position.
 *
 * A default the store has never opened in the dashboard has no row yet and
 * comes back with `id: null`. Defaults are bound by `section: <slug>` either
 * way; only a merchant's own collection is bound by `collectionId`.
 */
export interface EditorCollection {
  id: string | null;
  kind: "SYSTEM" | "CUSTOM";
  /** LATEST, TOP_SELLER, POPULAR or ON_SALE on a default; null on a custom list. */
  systemKey: string | null;
  name: string;
  slug: string;
  /** False when the merchant switched the list off: shoppers don't see it. */
  isActive: boolean;
  /** Defaults only: automatic picks fill in behind the pins. Null on a custom list. */
  fillWithAuto: boolean | null;
  /** Members with a published version — what a section can actually show. */
  publishedCount: number;
  /** The list's storefront page under this template's listing path. */
  href: string;
}

const text = (v: unknown): string => (typeof v === "string" ? v : "");

function rowsOf(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (raw === null || typeof raw !== "object") return null;
  const data = (raw as { data?: unknown }).data;
  return Array.isArray(data) ? data : null;
}

/**
 * The response, read into lists. `null` when it is not a list at all, so a
 * contract slip reads as "unknown", never as a store with no lists. A row
 * without a slug is dropped: nothing can bind to it.
 */
export function readEditorCollections(raw: unknown): EditorCollection[] | null {
  const rows = rowsOf(raw);
  if (!rows) return null;
  const out: EditorCollection[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const slug = text(r.slug);
    if (!slug) continue;
    const kind = r.kind === "SYSTEM" ? "SYSTEM" : "CUSTOM";
    const id = text(r.id) || null;
    // A custom list is only ever bound by id.
    if (kind === "CUSTOM" && !id) continue;
    out.push({
      id,
      kind,
      systemKey: text(r.systemKey) || null,
      name: text(r.name) || slug,
      slug,
      isActive: r.isActive !== false,
      fillWithAuto: typeof r.fillWithAuto === "boolean" ? r.fillWithAuto : null,
      publishedCount:
        typeof r.publishedCount === "number" && Number.isFinite(r.publishedCount)
          ? r.publishedCount
          : 0,
      href: text(r.href),
    });
  }
  return out;
}
